import { type DepartmentJSONObject, IkeUserRole, type OrgJSONObject } from '@ikegps/core-api';
import { IkeRestDepartment, Log } from '@ikegps/core-shared';
import { action, computed, type IObservableArray, observable, reaction, makeObservable } from 'mobx';
import { IkeSort } from '../sort';
import { type IkeClientService, Services } from './index';

export class DepartmentInviteService {
	constructor(protected log: Log) {}

	async request(emails: string[], role?: IkeUserRole): Promise<any> {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'User_invite:request',
			String(emails.length),
			1
		);
		await Services.Department.rest.Department.Invite.do({ emails, role });
		Services.UserProfile.refresh();
	}
}

export class DepartmentService implements IkeClientService {
	__priority = 50;

	constructor(protected log: Log) {
		makeObservable(this);
	}

	public Invite = new DepartmentInviteService(this.log);
	public departments: IObservableArray<DepartmentJSONObject> = observable.array<DepartmentJSONObject>([], {
		deep: false,
	});
	public writableDepartments: IObservableArray<DepartmentJSONObject> = observable.array<DepartmentJSONObject>([], {
		deep: false,
	});
	public adminDepartments: IObservableArray<DepartmentJSONObject> = observable.array<DepartmentJSONObject>([], {
		deep: false,
	});
	@observable current: DepartmentJSONObject | null = null;
	@observable currentOrg: OrgJSONObject | null = null;

	public rest: IkeRestDepartment;

	init() {
		reaction(
			() => Services.SSO.token,
			() => this.updateDepartments()
		);
		reaction(
			(_) => Services.UserSettings.departmentId,
			() => this.updateDepartments()
		);
		reaction(
			() => this.current,
			() => this.updateOrg()
		);

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.updateDepartments();
	}

	async setUserRole(userId: string, role: IkeUserRole): Promise<void> {
		const rest = Services.Department.rest;
		await rest.Department.User.role({ userId, role });
		Services.UserProfile.refresh();
	}

	@action async updateDepartments() {
		const token = Services.SSO.token;

		if (token == null) {
			this.departments.clear();
			this.adminDepartments.clear();
			this.writableDepartments.clear();
			this.current = null;
			this.rest = null;
			return Promise.resolve();
		}

		const [departments, adminDepartments, writeableDepartments] = await Promise.all([
			Services.User.rest.departments.fetch(),
			Services.User.rest.adminDepartments.fetch(),
			Services.User.rest.writeableDepartments.fetch(),
		]);

		this.setDepartments(departments);
		this.setAdminDepartments(adminDepartments);
		this.setWriteableDepartments(writeableDepartments);
	}

	@action async updateOrg() {
		if (this.current?.orgId) {
			this.currentOrg = await Services.User.rest.Org.getById({ orgId: this.current.orgId });
		}
	}

	@action setDepartment(departmentId: string) {
		const match = this.departments.find((x) => x.objectId === departmentId);

		if (match) {
			this.rest = Services.User.rest.bind(match.objectId);
			this.current = match;

			return;
		}

		throw new Error(`Invalid department: ${departmentId}`);
	}

	hasDepartment(departmentId: string): boolean {
		for (const department of this.departments) {
			if (department.objectId === departmentId) {
				return true;
			}
		}

		return false;
	}

	/** Check if the user is part of other departments of the same organization */
	@computed get otherDepartmentsInOrg(): DepartmentJSONObject[] {
		const current = this.current;
		if (current == null) {
			return [];
		}

		// No longer the case as all organizations have been back-filled. But still possible.
		if (current.orgId == null) {
			return [];
		}

		const otherDepartments: DepartmentJSONObject[] = [];
		for (const department of this.departments) {
			if (department.objectId !== current.objectId && department.orgId === current.orgId) {
				otherDepartments.push(department);
			}
		}

		return otherDepartments;
	}

	@action moveableDepartments(): DepartmentJSONObject[] {
		let moveableDepartments: DepartmentJSONObject[] = [];
		let isLicensedOrg = Boolean(this.currentOrg?.licenseCheckFrom);
		const isAnalyst = Services.User.isIkeAnalyst(Services.User.current);

		// If the user has elevated permissions, they can move to any department that they have write permissions to
		if (isLicensedOrg || isAnalyst) {
			this.writableDepartments.forEach((department) => {
				// Cant move to current department
				if (department.objectId === this.current?.objectId) {
					return;
				}

				moveableDepartments.push(department);
			});
		}

		// Then for every department that the user is in
		this.departments.forEach((department) => {
			// Cant move to current department
			if (department.objectId === this.current?.objectId) {
				return;
			}

			const isDepartmentOutsideOrg = department.orgId !== this.current?.orgId;

			// We don't want outside org departments (they are handled separately above)
			if (isDepartmentOutsideOrg) {
				return;
			}

			// If is already in the list then skip it
			if (moveableDepartments.find((value) => department.objectId === value.objectId)) {
				return;
			}

			moveableDepartments.push(department);
		});

		return moveableDepartments;
	}

	@action private setAdminDepartments(adminDepartments: DepartmentJSONObject[]) {
		this.adminDepartments.clear();

		IkeSort.byKey(adminDepartments, 'displayName', IkeSort.ASC);

		for (const department of adminDepartments) {
			this.adminDepartments.push(department);
		}
	}

	@action private setWriteableDepartments(writeableDepartments: DepartmentJSONObject[]) {
		this.writableDepartments.clear();

		IkeSort.byKey(writeableDepartments, 'displayName', IkeSort.ASC);

		for (const department of writeableDepartments) {
			this.writableDepartments.push(department);
		}
	}

	@action private setDepartments(departments: DepartmentJSONObject[]) {
		this.departments.clear();

		IkeSort.byKey(departments, 'displayName', IkeSort.ASC);

		if (!Services.User.hasUser) {
			this.current = null;
			this.rest = null;
			return;
		}

		const departmentId = Services.UserSettings.departmentId;

		for (const department of departments) {
			if (department.objectId === departmentId) {
				this.current = department;
			}
			this.departments.push(department);
		}

		const nextCurrent = this.current || this.pickDefaultDepartment(departments);

		if (nextCurrent) {
			this.rest = Services.User.rest.bind(nextCurrent.objectId);
		}

		if (this.current !== nextCurrent) {
			this.current = nextCurrent;
		}
	}

	private pickDefaultDepartment(departments: DepartmentJSONObject[]): DepartmentJSONObject {
		return departments[0] == null ? null : departments[0];
	}
}
